import { apiClient } from './config.js';

export interface ContentProduct {
  product_id: string;
  product_name: string;
  product_type: string;
  generation_config: Record<string, any>;
  quality_config: Record<string, any>;
  preparation_config: Record<string, any>;
  approval_mode: string;
  schedule_config: Record<string, any>;
  egress_target_id: string | null;
  egress_destination_id: string | null;
  egress_component_name: string | null;
  is_active: boolean;
  /** When true, a successful test_branch publish auto-enqueues a
   *  promote_article job for the same target. Honoured only for
   *  product_type IN ('article','intelligence_signal'). */
  auto_promote_to_live?: boolean;
  rule_count: number;
  article_count: number;
  created_at: string;
  updated_at: string;
}

export interface RoutingRule {
  rule_id: string;
  rule_name: string;
  ingress_source_id: string | null;
  content_product_id: string;
  match_criteria: Record<string, any>;
  priority: number;
  is_active: boolean;
  product_name: string;
  product_type: string;
  source_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface IngressSource {
  source_id: string;
  source_name: string;
  source_type: string;
  adapter_name?: string;
  source_config: Record<string, any>;
  schedule_config?: Record<string, any>;
  filter_rules: Record<string, any>;
  is_active: boolean;
  last_fetch_at?: string;
  fetch_statistics?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export async function listContentProducts(): Promise<ContentProduct[]> {
  const { data } = await apiClient.get('/admin/content/products');
  return data;
}

export async function getContentProduct(productId: string): Promise<ContentProduct> {
  const { data } = await apiClient.get(`/admin/content/products/${productId}`);
  return data;
}

export async function listRoutingRules(): Promise<RoutingRule[]> {
  const { data } = await apiClient.get('/admin/content/rules');
  return data;
}

export async function listIngressSources(): Promise<IngressSource[]> {
  const { data } = await apiClient.get('/admin/content/sources');
  return data;
}

/**
 * Update a content product. Only fields you pass are sent. Used by the
 * Pipeline System Config tab to flip auto_promote_to_live, etc.
 */
export async function updateContentProduct(
  productId: string,
  updates: Partial<Pick<ContentProduct,
    | 'product_name' | 'approval_mode' | 'is_active' | 'auto_promote_to_live'
    | 'egress_component_name' | 'egress_target_id' | 'egress_destination_id'
  >>,
): Promise<ContentProduct> {
  const { data } = await apiClient.put(`/admin/content/products/${productId}`, updates);
  return data;
}


export interface RemediateTextImageHeroesResult {
  status: string;
  candidate_count: number;
  remediated_count?: number;
  failed_count?: number;
  article_ids?: string[];
  results?: {
    remediated: Array<Record<string, any>>;
    failed: Array<Record<string, any>>;
  };
}

/**
 * Re-prepare images for articles whose featured image is a PDF page-render
 * (a text-as-image hero). Re-runs image selection only — article prose is
 * untouched. Synchronous and bounded by `limit`; call repeatedly until the
 * candidate count reaches zero.
 */
export async function remediateTextImageHeroes(
  dryRun: boolean,
  limit = 10,
): Promise<RemediateTextImageHeroesResult> {
  const { data } = await apiClient.post(
    '/admin/content/articles/remediate-text-image-heroes',
    null,
    { params: { dry_run: dryRun, limit } },
  );
  return data;
}
